import { CommonDialogs } from "@mcleod/common";
import { ClickEvent, DataSourceMode, Label, Layout, ListItem, MultiButton, SaveButton, SlideoutDecorator, Snackbar, Table, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { Alignment, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutMapBrokerageCodeList } from "./autogen/AutogenLayoutMapBrokerageCodeList";
import { GlaMapBrokerageCode } from "./GlaMapBrokerageCode";
import { MapBrokerageCode } from "./MapBrokerageCode";

const log = LogManager.getLogger("lme.dispatch.MapBrokerageCodeList");
const TYPE_STATE_TO_EXTERNAL_CODE = "S2E";
const TYPE_SCAC_TO_EXTERNAL_CODE = "S2S";
const TYPE_PACKAGING_TO_EXTERNAL_CODE = "PKG";

export class MapBrokerageCodeList extends AutogenLayoutMapBrokerageCodeList {

    private layoutMapBrokerageCode = Layout.getLayout("lme/dispatch/MapBrokerageCode") as MapBrokerageCode;
    private layoutGlaMapBrokerageCode = Layout.getLayout("lme/dispatch/GlaMapBrokerageCode") as GlaMapBrokerageCode;

    override onLoad() {
        this.tableMapBrokerageCode.addLayout = null;
        const multiButton = new MultiButton({
            id: "addBrokerageCodeMappingButton",
            caption: "Add Mapping(s)",
            dropdownItems: this.createDropdownLabels(),
            imageName: ImageName.add,
            color: "primary",
            hotkey: "Alt-A",
            onClick: () => { this.tableMapBrokerageCode.addLayout = "lme/dispatch/GlaMapBrokerageCode"; this.showAdd() },
            tooltip: "Add a new mapping",
            dropdownProps: {},
            borderWidth: 1,
            width: 200,
            height: 32
        });
        this.tableMapBrokerageCode.addRightTool(multiButton);
        this.overrideUrlParam();
    }

    private overrideUrlParam() {
        this.tableMapBrokerageCode.overrideUrlParams = (selectedRow: TableRow) => {
            return {
                key: selectedRow.data.get("code_source"),
                codeType: selectedRow.data.get("code_type"),
                fromCode: selectedRow.data.get("from_code")
            };
        };
    }

    tableMapBrokerageCodeOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const warningLabel: Label = tableRow.findComponentById("labelWarning") as Label;
        const fromError = modelRow.get("from_error_message");
        const toError = modelRow.get("to_error_message");
        warningLabel.visible = !!fromError || !!toError;
        const tooltipParts: string[] = [];
        if (fromError) tooltipParts.push(fromError);
        if (toError) tooltipParts.push(toError);
        warningLabel.tooltip = tooltipParts.length > 0 ? tooltipParts.join("\n\n") : "";
    }

    buttonFindErrorsOnClick(event: ClickEvent) {
        this.mainDataSource.search({ find_error: true });
    }

    private createDropdownLabels(): ListItem[] {
        const glaMapBrokerageLayout = "lme/dispatch/GlaMapBrokerageCode";
        const mapBrokerageLayout = "lme/dispatch/MapBrokerageCode";
        const tableMapBrokerage: Table = this.tableMapBrokerageCode;
        const mapClick = (code: string) => () => {
            tableMapBrokerage.addLayout = mapBrokerageLayout;
            this.addMapBrokerageSlideout(code);
        };
        return [
            new ListItem({ text: "GL Assignment", onClick: () => { tableMapBrokerage.addLayout = glaMapBrokerageLayout; this.showAdd() } }),
            new ListItem({ text: "States", onClick: mapClick(TYPE_STATE_TO_EXTERNAL_CODE)}),
            new ListItem({ text: "SCAC", onClick: mapClick(TYPE_SCAC_TO_EXTERNAL_CODE) }),
            new ListItem({ text: "Packaging", onClick: mapClick(TYPE_PACKAGING_TO_EXTERNAL_CODE) })
        ];
    }

    private addSaveAndCloseButton: SaveButton = SaveButton.create({
        id: "saveAndCloseButton",
        caption: "Save & Close",
        rowBreak: false,
        onClick: (event: ClickEvent) => {
            const selectedRow = this.tableMapBrokerageCode.selectedRow as TableRow;
            this.layoutMapBrokerageCode.mainDataSource.post().then((row) => {
                if (row) {
                    const rowArr: ModelRow[] = [];
                    rowArr.push(row);
                    this.tableMapBrokerageCode.dataSource.setRowsAndMode(DataSourceMode.NONE, rowArr);
                }
                this.layoutMapBrokerageCode.slideOut();
            })
        }
    })

    private async showAdd(type?: string) {
        this.layoutGlaMapBrokerageCode = Layout.getLayout("lme/dispatch/GlaMapBrokerageCode") as GlaMapBrokerageCode;
        this.layoutGlaMapBrokerageCode.saveButton = this.glaSaveAndCloseButton;
        const slideoutDecorator = new SlideoutDecorator({
            title: "Add - GL Assignment Mappings",
            layout: this.layoutGlaMapBrokerageCode,
            fillVerticalSpace: true,
            width: "100%",
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { greyedBackground: true },
            onClose: async (cancelled: boolean) => {
                if (!cancelled)
                    this.layoutGlaMapBrokerageCode.slideOut();
                if (this.layoutGlaMapBrokerageCode.tableMapBrokerageCode.dataSource.hasChanged()) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = {
                        yesButtonCaption: "Leave",
                        noButtonCaption: "Stay"
                    };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true)
                        return;
                }
                this.layoutGlaMapBrokerageCode.slideOut();
            },
            addlComponents: this.glaSaveAndCloseButton
        });
    }

    addMapBrokerageSlideout(type: string) {
        const layout = this.layoutMapBrokerageCode;
        const title = this.getMapBrokerageCodeSlideoutTitle(type);
        new SlideoutDecorator({
            title: title,
            layout: layout,
            fillVerticalSpace: true,
            width: 1000,
            overlayProps: { greyedBackground: true , closeOnClickOff: false},
            addlComponents: this.addSaveAndCloseButton,
            doAfterSlideIn: () => {
                layout.sourceMapBrokerageCode.mode = DataSourceMode.ADD;
                layout.setupPageByType(type);
            },
            onClose: async (cancelled: boolean) => {
                if (!cancelled)
                    layout.slideOut();
                if (layout.sourceMapBrokerageCode.hasChanged(true)) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true) {
                        return;
                    }
                }
                layout.slideOut();
            }
        });
    }


    private getMapBrokerageCodeSlideoutTitle(type: string): string {
        const titleMap: Record<string, string> = {S2E: "Add - State Mapping", S2S: "Add - SCAC Mapping", PKG: "Add - Packaging Mapping"};
        return titleMap[type] ?? "Add - Map Rating Code Mapping";
    }

    private glaSaveAndCloseButton: SaveButton = SaveButton.create({
        id: "saveAndCloseButton",
        caption: "Save & Close",
        rowBreak: false,
        enabled: false,
        onClick: async (event: ClickEvent) => {
            if (StringUtil.isEmptyString(this.layoutGlaMapBrokerageCode.textboxGlaCode.text)) {
                Snackbar.showSnackbar("GLA Code is required");
                return;
            }
            else if (this.layoutGlaMapBrokerageCode.tableMapBrokerageCode.allRows.length < 1) {
                Snackbar.showSnackbar("Please Display Mappings prior to saving");
                return;
            }
            const rowArr: ModelRow[] = this.layoutGlaMapBrokerageCode.tableMapBrokerageCode.data as ModelRow[];
            const addedRowArr: ModelRow[] = [];
            const errorRows: string[] = [];
            const promises = rowArr.map((row) => this.saveRow(row));
            await Promise.allSettled(promises).then(async (results) => {
                results.forEach((result) => {
                    if (result.status === "fulfilled") {
                        addedRowArr.push(result.value);
                    } else {
                        errorRows.push(result.reason.messages[0]);
                        log.error("An error occurred while saving map rating code", result.reason);
                    }
                });
                this.tableMapBrokerageCode.dataSource.setRowsAndMode(DataSourceMode.NONE, addedRowArr);
                if (addedRowArr.length > 0 && errorRows.length > 0) {
                    this.layoutGlaMapBrokerageCode.slideOut().then(() => {
                        errorRows.forEach((error) => Snackbar.showWarningSnackbar(error, { persist: true }));
                    });
                }
                else if (addedRowArr.length > 0 && errorRows.length === 0)
                    this.layoutGlaMapBrokerageCode.slideOut();
                else if (addedRowArr.length === 0 && errorRows.length > 0)
                    CommonDialogs.showDialog(errorRows).then(() => {
                        this.layoutGlaMapBrokerageCode.slideOut();
                    })
            })
        }
    })

    private saveRow(row: ModelRow): Promise<ModelRow<any>> {
        return new Promise((resolve, reject) => {
            row.post().then((response) => {
                resolve(response);
            }).catch((err) => {
                log.error("An error occurred while saving map rating code", err);
                reject(err);
            });
        });
    }
}
